<template>
    <or-modal
        ref="modal"
        dismiss-on="close-button"
        class="utterance--autogenerated-entities"
        :title="$t('common.modals.utterance_auto_prediction.utterances_auto_prediction_modal.confirmEntities')"
        @close="handleCancel"
    >
        <div class="container">
            <div class="phrase-entities">
                <p class="phrase-entities-header">
                  {{ $t('generic.phrase') }}
                </p>
                <div class="editor-wrap">
                    <entity-token
                        v-for="(token, $index) in tokens"
                        :key="$index"
                        :index="$index"
                        :token="token"
                        :entities="entities"
                    />
                </div>
            </div>
            <div class="auto-predict-actions">
                <or-button
                    class="cancel-btn"
                    type="secondary"
                    :loading="utterances.loading || (autoPredictedUtterance && autoPredictedUtterance.loading)"
                    @click="handleCancel"
                >
                  {{ $t('generic.cancel') }}
                </or-button>

                <or-button
                    color="primary"
                    :loading="utterances.loading || (autoPredictedUtterance && autoPredictedUtterance.loading)"
                    @click="handleConfirm"
                >
                  {{ $t('generic.confirm') }}
                </or-button>
            </div>
        </div>
    </or-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { MachineLearningLabelingService } from '@/services/utterance/machine-learning-labeling.service'
import { MachineLearningTestPreviewService } from '@/services/utterance/machine-learning-test-preview.service'
import EntityToken from './autopredict-entity-token'
import { ENTITY_TYPE } from '@/components/model/nlu-model/sidebar/entities/entity-types'
import _ from 'lodash'

function indexToToken (text, index) {
  const tokens = text
    .slice(0, index + 1)
    .match(/(([^\x00-\x7F]|\w)+|\S)\s*/giu) || [] // eslint-disable-line no-control-regex

  return tokens.length - 1
}

export default {
  components: {
    EntityToken
  },

  data () {
    return {
      localUtterance: null
    }
  },

  computed: {
    ...mapState('knowledgebase', ['utterances']),

    autoPredictedUtterance () {
      const queue = this.utterances.autoPredictedUtterancesQueue
      return queue.length !== 0 ? queue[0] : null
    },

    tokens () {
      if (this.localUtterance) {
        return MachineLearningLabelingService.extractTokensFromText(this.localUtterance.text)
      }
      return []
    },

    entities () {
      const transformMlEntity = mlEntity => ({
        entity: mlEntity.entity,
        start: indexToToken(this.localUtterance.text, mlEntity.startPos),
        end: indexToToken(this.localUtterance.text, mlEntity.endPos),
        type: ENTITY_TYPE.machineLearning,
        children: mlEntity.children ? mlEntity.children.map(child => transformMlEntity(child)) : [],
        role: mlEntity.role || undefined
      })

      const machineLearningEntities = this.localUtterance.entities
        .map(el => transformMlEntity(el))

      const entityPredictions = this.localUtterance.entityPredictions
        .map(el => ({
          entity: el.entityName,
          start: el.startTokenIndex,
          end: el.endTokenIndex,
          role: el.role || undefined,
          type: 'auto'
        }))
      return _.concat(machineLearningEntities, entityPredictions)
    }
  },

  methods: {
    ...mapActions('knowledgebase/utterances', ['updateUtterance', 'removeFromAutoPredictionQueue']),

    handleCancel () {
      if (this.autoPredictedUtterance) {
        this.updateUtterance({
          data: this.autoPredictedUtterance.utterance,
          appId: this.autoPredictedUtterance.appId
        })
        this.$refs.modal.close()
      }
    },

    handleConfirm () {
      this.updateUtterance({
        data: this.localUtterance,
        appId: this.autoPredictedUtterance.appId
      })
      this.$refs.modal.close()
    }
  },

  watch: {
    autoPredictedUtterance (utteranceAndAppId) {
      if (utteranceAndAppId) {
        this.localUtterance = _.cloneDeep(utteranceAndAppId.utterance)
        const mlEntities = this.localUtterance.entityPredictions
          .filter(e => e.type === ENTITY_TYPE.machineLearning)
        this.localUtterance.entities = MachineLearningTestPreviewService.removeForbiddenParams(mlEntities)
        const notMlEntities = this.localUtterance.entityPredictions
          .filter(e => e.type !== ENTITY_TYPE.machineLearning)
        this.localUtterance.entityPredictions = MachineLearningTestPreviewService.removeForbiddenParams(notMlEntities)

        this.$refs.modal.open()
      } else {
        this.$refs.modal.close()
      }
    }
  }
}
</script>

<style scoped lang="scss">
    .utterance--autogenerated-entities {
        z-index: 1000;
        .container {
            min-height: 300px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            .phrase-entities {
                .phrase-entities-header {
                    margin: $sp-1 0;
                    color: rgb(145, 150, 157);
                    font-size: 12px;
                    font-weight: normal;
                }
            }
        }
        .editor-wrap {
            min-height: 128px;
            padding: 8px 10px;
            border-radius: 4px;
            border: 1px solid rgb(223, 223, 223);
            background: rgb(246, 246, 246);
        }
        .auto-predict-actions {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            margin: 20px 0 12px;
            .cancel-btn {
                margin-right: 10px;
            }
        }
    }
</style>
